import { useKeyboardContext } from '@/core/keyboard';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePosSession } from '@/hooks/usePosSession';
import { usePosHardware } from '@/pages/pos/PosHardwareContext';

function statusDot(ok: boolean) {
  return ok ? 'text-emerald-400' : 'text-slate-500';
}

export function PosStatusBar() {
  const { focusZone, statusHint } = useKeyboardContext();
  const store = useAppSelector((s) => s.app.selectedStore);
  const user = useAppSelector((s) => s.auth.user);
  const { activeShiftId } = usePosSession();
  const { config, prefs, barcodePrinterReady } = usePosHardware();

  const scaleOn = !!config?.scaleEnabled && !!prefs.scaleConnected;
  const labelOn = barcodePrinterReady;
  const receiptOn = !!config?.thermalPrinterEnabled && !!prefs.receiptPrinterConnected;
  const displayOn = !!config?.customerDisplayEnabled;

  return (
    <footer className="flex h-9 shrink-0 items-center justify-between border-t border-slate-300 bg-slate-800 px-3 text-xs text-slate-200">
      <div className="flex items-center gap-4">
        <span className="font-medium text-emerald-400">● Billing</span>
        <span className={activeShiftId ? 'text-emerald-400' : 'text-amber-400'}>
          {activeShiftId ? 'Shift open' : 'No shift'}
        </span>
        <span className="text-slate-400">Focus:</span>
        <span className="capitalize text-white">{focusZone}</span>
        {statusHint ? (
          <span className="hidden truncate text-slate-300 lg:inline max-w-md">{statusHint}</span>
        ) : null}
      </div>
      <div className="flex items-center gap-3 text-slate-400">
        <span className="hidden md:flex items-center gap-2">
          <span className={statusDot(true)} title="Barcode scanner (USB HID)">Scan</span>
          {config?.scaleEnabled && <span className={statusDot(scaleOn)} title="Weighing scale">Scale</span>}
          {config?.barcodePrinterEnabled && <span className={statusDot(labelOn)} title="Label printer">Label</span>}
          {config?.thermalPrinterEnabled !== false && (
            <span className={statusDot(receiptOn)} title="Receipt printer">Receipt</span>
          )}
          {displayOn && <span className={statusDot(displayOn)} title="Customer display">Display</span>}
        </span>
        <span>
          {store?.name} · {user?.firstName}
        </span>
        <span className="hidden sm:inline text-slate-400">Open Guide for keyboard shortcuts</span>
      </div>
    </footer>
  );
}
